import * as React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, FormControlLabel, Switch, TextField } from '@mui/material';
import {
    GridPanelContent,
    GridPanelFooter,
    GridPanelHeader,
    GridPanelWrapper,
    gridColumnDefinitionsSelector,
    gridColumnVisibilityModelSelector,
    useGridApiContext,
    useGridRootProps,
} from '@mui/x-data-grid-pro';
import { useGridSelector } from '@mui/x-data-grid';

const collator = new Intl.Collator();

const defaultSearchPredicate = (column, searchValue) => (
    (column.headerName || column.field).toLowerCase().includes(searchValue)
);

const TripUserColumnsPanel = (props) => {
    const {
        onResetClick,
        sort,
        searchPredicate = defaultSearchPredicate,
        autoFocusSearchField = true,
        ...other
    } = props;

    const apiRef = useGridApiContext();
    const rootProps = useGridRootProps();
    const searchInputRef = React.useRef(null);
    const firstSwitchRef = React.useRef(null);
    const columns = useGridSelector(apiRef, gridColumnDefinitionsSelector);
    const columnVisibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector);
    const [searchValue, setSearchValue] = React.useState('');

    const sortedColumns = React.useMemo(() => {
        switch (sort) {
            case 'asc':
                return [...columns].sort((a, b) => collator.compare(a.headerName || a.field, b.headerName || b.field));
            case 'desc':
                return [...columns].sort((a, b) => -collator.compare(a.headerName || a.field, b.headerName || b.field));
            default:
                return columns;
        }
    }, [columns, sort]);

    const currentColumns = React.useMemo(() => {
        if (!searchValue) return sortedColumns;
        const q = searchValue.toLowerCase();
        return sortedColumns.filter((column) => searchPredicate(column, q));
    }, [sortedColumns, searchValue, searchPredicate]);

    const toggleColumn = (event) => {
        const { name: field } = event.target;
        apiRef.current.setColumnVisibility(field, columnVisibilityModel[field] === false);
    };

    const toggleAllColumns = React.useCallback((isVisible) => {
        const currentModel = gridColumnVisibilityModelSelector(apiRef);
        const newModel = { ...currentModel };
        columns.forEach((col) => {
            if (col.hideable) {
                if (isVisible) {
                    delete newModel[col.field];
                } else {
                    newModel[col.field] = false;
                }
            }
        });
        apiRef.current.setColumnVisibilityModel(newModel);
    }, [apiRef, columns]);

    React.useEffect(() => {
        if (autoFocusSearchField) {
            searchInputRef.current?.focus();
        } else if (firstSwitchRef.current?.focus) {
            firstSwitchRef.current.focus();
        }
    }, [autoFocusSearchField]);

    let firstHideableColumnFound = false;
    const isFirstHideableColumn = (column) => {
        if (!firstHideableColumnFound && column.hideable !== false) {
            firstHideableColumnFound = true;
            return true;
        }
        return false;
    };

    const BaseTextField = rootProps.slots?.baseTextField || rootProps.components?.BaseTextField || TextField;
    const BaseSwitch = rootProps.slots?.baseSwitch || rootProps.components?.BaseSwitch || Switch;
    const baseTextFieldProps = rootProps.slotProps?.baseTextField || rootProps.componentsProps?.baseTextField;
    const baseSwitchProps = rootProps.slotProps?.baseSwitch || rootProps.componentsProps?.baseSwitch;

    return (
        <GridPanelWrapper {...other}>
            <GridPanelHeader>
                <BaseTextField
                    label={apiRef.current.getLocaleText('columnsPanelTextFieldLabel')}
                    placeholder={apiRef.current.getLocaleText('columnsPanelTextFieldPlaceholder')}
                    inputRef={searchInputRef}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    variant="standard"
                    fullWidth
                    {...baseTextFieldProps}
                />
            </GridPanelHeader>
            <GridPanelContent>
                <Box sx={{ py: 1, pl: 1 }}>
                    {currentColumns.map((column) => (
                        <Box
                            key={column.field}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                py: '1px',
                                pl: '7px',
                                pr: 1,
                            }}
                        >
                            <FormControlLabel
                                control={(
                                    <BaseSwitch
                                        disabled={column.hideable === false}
                                        checked={columnVisibilityModel[column.field] !== false}
                                        onClick={toggleColumn}
                                        name={column.field}
                                        size="small"
                                        inputRef={isFirstHideableColumn(column) ? firstSwitchRef : undefined}
                                        {...baseSwitchProps}
                                    />
                                )}
                                label={column.headerName || column.field}
                            />
                        </Box>
                    ))}
                </Box>
            </GridPanelContent>
            <GridPanelFooter sx={{ gap: 1 }}>
                <Button size="small" onClick={() => toggleAllColumns(false)}>
                    {apiRef.current.getLocaleText('columnsPanelHideAllButton')}
                </Button>
                <Button size="small" onClick={onResetClick}>
                    Reset
                </Button>
                <Button size="small" onClick={() => toggleAllColumns(true)}>
                    {apiRef.current.getLocaleText('columnsPanelShowAllButton')}
                </Button>
            </GridPanelFooter>
        </GridPanelWrapper>
    );
};

TripUserColumnsPanel.propTypes = {
    onResetClick: PropTypes.func.isRequired,
    sort: PropTypes.oneOf(['asc', 'desc']),
    searchPredicate: PropTypes.func,
    autoFocusSearchField: PropTypes.bool,
};

export default TripUserColumnsPanel;
